import type { Signal } from '@preact/signals-react';
import { useSignalEffect } from '@preact/signals-react';
import clsx from 'clsx';
import { cloneDeep, debounce } from 'lodash';
import type React from 'react';
import type { CSSProperties, ReactNode, Ref } from 'react';
import { useCallback, useEffect, useImperativeHandle, useReducer, useRef, useState } from 'react';
import { Observable, Subject, skipWhile, takeUntil } from 'rxjs';
import type {
  JsonDataWrapper,
  OdinDataFilter,
  OdinDataRetrieval,
  OdinDataRetrievalOptions,
  OdinDataSort,
} from '../../common/data-retrieval/data-retrieval-interfaces';
import { OdinDataSender } from '../../common/data-retrieval/odin-data-sender';
import { deepEquals } from '../../common/functions/deep-equals';
import { useEventDebounce } from '../../common/hooks/use-event';
import type { OdinKey } from '../../common/types/odin-key.type';
import { cloneObject, findDataIndex, isEmptyObject } from '../common-functions';
import type { SearchValueType, TypeOrArray } from '../common-interfaces';
import { DataSearchLocation } from '../enums';
import type { ActionSectionItem } from '../fields/drop-down/drop-down-action-section.component';
import type { JsonData, JsonDataItem } from '../generic-types';
import type { ContinuousScrollerReducerAction, ContinuousScrollerState } from './continuous-scroller.reducer';
import reducer, { ContinuousScrollerActionType } from './continuous-scroller.reducer';
import {
  convertComparisonTypeToString,
  filterData,
  getComparisonType,
  getValidSearchFilters,
  sortData,
} from './continuous-scroller-functions';
import type {
  ContinuousScrollerFilter,
  ContinuousScrollerSort,
  ScrollerChildRenderFunction,
  ScrollerData,
  ScrollerDataItem,
  ScrollerDataLoadInspectorFunction,
  ScrollerDataModificationFunction,
  ScrollerItemsChangedFunction,
} from './continuous-scroller-types';
import { ContinuousScrollerSortDirection } from './continuous-scroller-types';
import ScrollerPageMarker from './scroller-page-marker.component';
import ScrollingDataPage from './scrolling-data-page.component';

export interface ContinuousScrollerProps {
  ref?: Ref<ContinuousScrollerRef>;
  className?: string;
  scrollingElement?: Element | null;
  dataRetriever: OdinDataRetrieval;
  /**
   * The number of pages to render above and below the current page.
   */
  surroundingPagesCount?: number;
  pageSize?: number;
  /**
   * Used to determine the minimum number of items that will be considered a "page"; if the next potential page is less
   * than this number then it will be appended to the previous page.
   */
  minimumPageCount?: number;
  stopPageRequestOnZeroPage?: boolean;
  /**
   * Tells the component the name of the unique id field for each data item; this will be used for updating data.
   */
  idFieldName?: string;
  canUpdateData?: boolean;
  /**
   * Determines if a getData call should be made when the data retrieval options haven't changed.
   */
  canMakeDuplicateCall?: boolean;
  /**
   * Tells the component to associate record id's with the pages they were requested in. This will be used to allow a
   * getData call to be made again once a record has been updated outside the component.
   */
  useDataCache?: boolean;
  dataLoadIndicator?: React.JSX.Element;
  filterSignal?: Signal<Array<ContinuousScrollerFilter>>;
  sortSignal?: Signal<Array<ContinuousScrollerSort>>;
  /**
   * The injected function used to locally filter data in the continuous scroller; this can be passed in by the user to
   * provide custom filtering logic.
   * @param {ScrollerData} data - The unfiltered data.
   * @param {Array<ContinuousScrollerFilter>} filters - The filters to apply to the data.
   * @returns {ScrollerData} The filtered data.
   */
  filterFunction?: (data: ScrollerData, filters: Array<ContinuousScrollerFilter>) => ScrollerData;
  /**
   * The injected function used to locally sort data in the continuous scroller; this can be passed in by the user to
   * provide custom sorting logic.
   * @param {ScrollerData} data - The unsorted data.
   * @param {Array<ContinuousScrollerSort>} sorts - The sorts to apply to the data.
   * @returns {ScrollerData} The sorted data.
   */
  sortFunction?: (data: ScrollerData, sorts: Array<ContinuousScrollerSort>) => ScrollerData;
  searchLocation?: DataSearchLocation;
  sortLocation?: DataSearchLocation;
  /**
   * Can be used by a calling component to inspect the data and do something with it. This will only be called when the
   * page data is actually returned from the API.
   * @param {number} page - The current page this data represents.
   * @param {JsonData} data - The pages actual data.
   */
  dataLoadInspector?: ScrollerDataLoadInspectorFunction;
  /**
   * Used to modify data coming from the API; this should be used to add custom properties to the use for use by other
   * components.
   * @param {JsonData} data - The data returned from the front-end.
   */
  modifyData?: ScrollerDataModificationFunction;
  itemsChanged?: ScrollerItemsChangedFunction;
  scrollerStyles?: CSSProperties;
  children: ScrollerChildRenderFunction;
  archivedField?: string;
  actionItems?: Array<ActionSectionItem>;
  isPaginated?: boolean;
}

export interface ContinuousScrollerRef {
  /**
   * Removes a row of data from the scroller; the assumption will be there is only one item with the searched for value.
   * This will be for the sake of performance.
   * @param {SearchValueType} value - The value to search for and remove.
   */
  removeItem: (value: SearchValueType) => void;

  /**
   * Gets all the data in all the pages.
   * @returns {JsonData} - The combined data for all pages.
   */
  getAllData: () => JsonData;

  /**
   * Sorts the locally loaded data; this will only work if either the `searchLocation` or `sortLocation` properties are
   * set to `Local` and there is at least 1 defined sort.
   */
  sortLocalData: () => void;

  /**
   * When a record is updated outside the component this tells the component which record was changed so that it can
   * attempt to update itself.
   * @param {OdinKey} recordId
   */
  recordChanged: (recordId: OdinKey) => void;

  /**
   * Makes a call through the getData function and clears the current page data.
   */
  getDataOverwritePageCache: () => void;

  /**
   * Calls the `getData` function with the previously used call options. The main purpose of this is the select all
   * feature.
   * @param {boolean} resetRenderPages - Determines if the render pages need to be recalculated as part of this request.
   */
  recallGetData: (resetRenderPages?: boolean) => void;

  /**
   * Stops the scroller from making requests when it reaches the end of the page.
   */
  stopPageRequests: () => void;

  /**
   * !!! Do not use this method unless absolutely necessary !!!
   *
   * Resets the scroller data.
   */
  __dangerouslyResetScrollerData: () => void;

  /**
   * Returns the continuous scroller DOM element client width.
   */
  getClientWidth: () => number | undefined;

  /**
   * Returns the continuous scroller container reference.
   */
  getContainerReference: () => HTMLElement | null;
}

function ContinuousScroller(props: ContinuousScrollerProps) {
  const {
    ref,
    className,
    scrollingElement = null,
    dataRetriever,
    surroundingPagesCount = 1,
    pageSize = 50,
    minimumPageCount = 15,
    stopPageRequestOnZeroPage = true,
    idFieldName = 'id',
    canUpdateData = false,
    canMakeDuplicateCall = false,
    useDataCache = false,
    dataLoadIndicator,
    filterSignal,
    sortSignal,
    filterFunction = filterData,
    sortFunction = sortData,
    searchLocation = DataSearchLocation.Local,
    sortLocation,
    dataLoadInspector,
    modifyData,
    itemsChanged,
    scrollerStyles,
    children,
    archivedField,
    actionItems,
    isPaginated = true,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | undefined>(undefined);
  const resizeObserver = useRef<ResizeObserver | undefined>(undefined);
  const dataResult = useRef<Observable<JsonDataWrapper> | undefined>(undefined);
  const dataResultSubscriber = useRef<OdinDataSender<JsonDataWrapper> | undefined>(undefined);
  const destroySubject = useRef<Subject<void> | undefined>(undefined);
  const loadedData = useRef<ScrollerData>([]);
  const updatedIndices = useRef<Array<number>>([]);
  const removeItemIndex = useRef<number | undefined>(undefined);
  const nextPageIndex = useRef<number | undefined>(undefined);
  const pageHeights = useRef<Record<number, number>>({});
  const currentPage = useRef(1);
  const requestedPage = useRef(1);
  const invalidatedPage = useRef<number | undefined>(undefined);
  const lastFulfilledPage = useRef(0);
  const totalPageCount = useRef(0);
  const savedFieldFilters = useRef<Record<string, OdinDataFilter> | undefined>(undefined);
  const savedSortedFields = useRef<Record<string, OdinDataSort> | undefined>(undefined);
  const previousCallOptions = useRef<OdinDataRetrievalOptions | undefined>(undefined);
  const calculatedRenderPages = useRef(new Map<number, ScrollerData>());
  const pageIdCache = useRef(new Map<number, Array<number | string>>());
  // when the component is waiting for data a call to getData shouldn't be allowed to happen again
  const allowGetDataCall = useRef(true);
  const areFiltersChanged = useRef(false);
  const areSortsChanged = useRef(false);
  const overwritePageCache = useRef(false);
  // set to true when the filter location is API, the filters have changed and the data response is an empty array
  const shouldRenderEmptyData = useRef(false);
  // set to true when the data retriever returns a zero page
  const isLastPageLoaded = useRef(false);
  // the last request id sent out for an API search request
  const lastRequestId = useRef<string | undefined>(undefined);
  // used to make sure an observed marker only causes a render the first time its observed
  const lastObserverMarker = useRef<string | undefined>(undefined);
  // this is used to store custom properties that will be returned to the caller as is
  const customProperties = useRef<JsonDataItem | undefined>(undefined);

  // Ref pattern to capture latest getData without recreating the debounced _callGetData callback.
  // This avoids resetting the debounce timer on every dataRetriever change while ensuring
  // the debounced function always calls the current getData implementation.
  const getDataRef = useRef(dataRetriever.getData);

  const [renderTrigger, setRenderTrigger] = useState(false);

  const initialState: ContinuousScrollerState = {
    isLoading: false,
  };

  const [state, dispatcher] = useReducer(reducer, initialState);

  useEffect(() => {
    getDataRef.current = dataRetriever.getData;
  }, [dataRetriever.getData]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional: mount-only effect for observable subscription setup
  useEffect(() => {
    resizeObserver.current = new ResizeObserver(resizeCallback);

    destroySubject.current = new Subject<void>();
    // Capture ref value after creation for cleanup
    const destroySubjectCurrent = destroySubject.current;
    dataResult.current = new Observable<JsonDataWrapper>((subscriber) => {
      dataResultSubscriber.current = new OdinDataSender(subscriber);
      dataRetriever.getSubscriber?.(dataResultSubscriber.current);
    });
    dataResult.current
      ?.pipe(
        skipWhile(
          (dataWrapper) =>
            lastRequestId.current !== undefined && dataWrapper && dataWrapper.requestId !== lastRequestId.current
        ),
        takeUntil(destroySubject.current)
      )
      .subscribe((dataWrapper) => {
        lastRequestId.current = undefined;
        customProperties.current = dataWrapper?.customProperties;

        // if no data is returned the only other reason we would want to allow the render to happen is if the getData call
        // happened because of a filter change and the search location is API. This will require the search results to be
        // sent back through this component.
        if (
          dataWrapper?.data &&
          (dataWrapper.data.length > 0 ||
            (searchLocation === DataSearchLocation.Api && (areFiltersChanged.current || areSortsChanged.current)))
        ) {
          const apiData = cloneDeep(dataWrapper.data).map<ScrollerDataItem>((item) => {
            return {
              __uniqueId: crypto.randomUUID(),
              ...item,
            };
          });
          // the api data needs to be modified before it's assigned to the loadedData variable
          modifyData?.(requestedPage.current, apiData);

          shouldRenderEmptyData.current = false;
          let resetData = false;
          if (!areFiltersChanged.current && !areSortsChanged.current && !overwritePageCache.current) {
            if (!canUpdateData) {
              loadedData.current.push(...apiData);
            } else {
              updateData(apiData, idFieldName);
            }
            lastFulfilledPage.current = requestedPage.current;

            if (useDataCache) {
              const setPage = invalidatedPage.current || requestedPage.current;
              pageIdCache.current.set(
                setPage,
                apiData.map((item) => item[idFieldName] as OdinKey)
              );
              // once the invalidated page has been reloaded set it to undefined so the next data load will use the
              // requested page
              invalidatedPage.current = undefined;
            }
          } else {
            shouldRenderEmptyData.current =
              apiData.length === 0 && searchLocation === DataSearchLocation.Api && areFiltersChanged.current;
            resetData = true;
            resetScrollerData(apiData);
          }

          totalPageCount.current = calculateTotalPageCount();
          calculateRenderData(resetData);
          calculateRenderPages();
          dataLoadInspector?.(requestedPage.current, apiData);
          itemsChanged?.(loadedData.current);
          setIsDataLoading(false);
        } else if (stopPageRequestOnZeroPage) {
          dataLoadInspector?.(requestedPage.current, []);
          allowGetDataCall.current = false;
          isLastPageLoaded.current = true;
          setIsDataLoading(false, false, false);
        } else {
          dataLoadInspector?.(requestedPage.current, []);
          setIsDataLoading(false);
        }
      });

    const validFilters = getValidSearchFilters([...(filterSignal?.value || [])]);
    if (
      (loadedData.current.length === 0 || totalPageCount.current === 0) &&
      dataResultSubscriber.current &&
      (!validFilters || validFilters.length === 0)
    ) {
      checkCallGetData({ page: 1, pageSize });
    }

    return () => {
      _callGetData.cancel();
      setIsDataLoading(false);
      observerRef.current?.disconnect();
      resizeObserver.current?.disconnect();

      destroySubjectCurrent?.next();
      destroySubjectCurrent?.complete();
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional: intersectionCallback is defined inline and stable
  useEffect(() => {
    observerRef.current = new IntersectionObserver(intersectionCallback, {
      root: scrollingElement,
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [scrollingElement]);

  useEffect(() => {
    nextPageIndex.current = Math.trunc(pageSize * 0.6);
  }, [pageSize]);

  useSignalEffect(() => {
    const validSearchFilters = getValidSearchFilters([...(filterSignal?.value || [])]);

    if (
      searchLocation === DataSearchLocation.Local &&
      loadedData.current.length > 0 &&
      validSearchFilters &&
      validSearchFilters.length > 0
    ) {
      const filteredResults = filterFunction(loadedData.current, validSearchFilters);

      itemsChanged?.(filteredResults);
      dispatcher({
        type: ContinuousScrollerActionType.SetSearchResults,
        searchResults: filteredResults,
      });
      dataLoadInspector?.(1, filteredResults);
    } else if (searchLocation === DataSearchLocation.Api) {
      if (dataResultSubscriber.current) {
        const hasArchivedFilterEnabled = Boolean(
          archivedField && actionItems && actionItems.some((item) => item.value === archivedField)
        );
        let isArchivedFilterChecked = false;
        if (actionItems && archivedField) {
          isArchivedFilterChecked = actionItems.find((item) => item.value === archivedField)?.selected ?? false;
        }
        const fieldFilters: Record<string, OdinDataFilter> = hasArchivedFilterEnabled
          ? {
              archived: {
                value: isArchivedFilterChecked,
                comparison: '=',
              },
            }
          : {};
        if (validSearchFilters) {
          for (let i = 0, length = validSearchFilters.length; i < length; i++) {
            fieldFilters[validSearchFilters[i].field] = {
              value: validSearchFilters[i].value,
              comparison: convertComparisonTypeToString(
                getComparisonType(validSearchFilters[i].value, validSearchFilters[i].comparison)
              ),
              customDataProperties: validSearchFilters[i].customDataProperties,
            };
          }
        }

        const areFiltersEmpty = isEmptyObject(fieldFilters);
        savedFieldFilters.current = !areFiltersEmpty ? fieldFilters : undefined;

        // if a filter has a value and then is removed the checks above won't consider that a change, so this code will
        // determine if the change was removing a filter
        areFiltersChanged.current = !deepEquals(previousCallOptions.current?.fieldFilters || {}, fieldFilters);

        lastRequestId.current = !areFiltersEmpty && areFiltersChanged.current ? crypto.randomUUID() : undefined;
        const options: OdinDataRetrievalOptions = {
          page: 1,
          pageSize,
          requestId: lastRequestId.current,
        };

        if (!areFiltersEmpty) {
          options.fieldFilters = savedFieldFilters.current;
        }

        options.sortedFields = savedSortedFields.current;

        if (areFiltersChanged.current) {
          checkCallGetData(options, true);
        }
      }
    } else {
      itemsChanged?.(loadedData.current);
      dispatcher({
        type: ContinuousScrollerActionType.SetSearchResults,
        searchResults: undefined,
      });
      // There is an issue where if the user is past the first couple of pages of regularly loaded data and performs a
      // local search and then clears that search, the paged data will load blank since the first page is not currently
      // rendered. I've attempted to fix this problem, but after a good amount of research the only potential way I can
      // think of to fix this issue is to have the search results and the regular items in 2 different containers and
      // change their z-indexes.
      currentPage.current = 1;
      calculateRenderPages();
    }
  });

  const hasLocalSortFields = (sortedFields: Array<ContinuousScrollerSort>) => {
    return (
      (sortLocation === DataSearchLocation.Local ||
        (sortLocation === undefined && searchLocation === DataSearchLocation.Local)) &&
      loadedData.current.length > 0 &&
      sortedFields.length > 0
    );
  };

  useSignalEffect(() => {
    const sortedFields = sortSignal?.value;

    // when sortLocation is not passed then searchLocation is used instead; this is the way the code previously worked
    if (sortedFields && hasLocalSortFields(sortedFields)) {
      const filteredResults = sortFunction(loadedData.current, sortedFields);

      itemsChanged?.(filteredResults);
      dispatcher({
        type: ContinuousScrollerActionType.SetSearchResults,
        searchResults: filteredResults,
      });
      dataLoadInspector?.(1, filteredResults);
    } else if (
      sortLocation === DataSearchLocation.Api ||
      (sortLocation === undefined && searchLocation === DataSearchLocation.Api)
    ) {
      if (dataResultSubscriber.current) {
        const fieldSorts: Record<string, OdinDataSort> = {};
        if (sortedFields) {
          for (let i = 0, length = sortedFields.length; i < length; i++) {
            fieldSorts[sortedFields[i].field] = {
              direction: sortedFields[i].sortDirection === ContinuousScrollerSortDirection.Ascending ? 'asc' : 'desc',
              order: sortedFields[i].order || 0,
              customDataProperties: sortedFields[i].customDataProperties,
            };
          }
        }

        const areSortsEmpty = isEmptyObject(fieldSorts);
        savedSortedFields.current = !areSortsEmpty ? fieldSorts : undefined;

        const options: OdinDataRetrievalOptions = {
          page: 1,
          pageSize,
          requestId: lastRequestId.current,
        };

        if (!areSortsEmpty) {
          options.sortedFields = savedSortedFields.current;
        }

        options.fieldFilters = savedFieldFilters.current;
        areSortsChanged.current = !deepEquals(previousCallOptions.current?.sortedFields || {}, fieldSorts);

        if (areSortsChanged.current) {
          checkCallGetData(options, true);
        }
      }
    }
  });

  const resetScrollerData = useCallback(
    (data: ScrollerData) => {
      loadedData.current = [...data];
      areFiltersChanged.current = false;
      areSortsChanged.current = false;
      overwritePageCache.current = false;
      allowGetDataCall.current = true;

      isLastPageLoaded.current = currentPage.current === totalPageCount.current;

      requestedPage.current = 1;
      lastFulfilledPage.current = 1;
      currentPage.current = 1;
      calculatedRenderPages.current.clear();

      if (useDataCache) {
        pageIdCache.current.clear();
        pageIdCache.current.set(
          1,
          data.map((item) => item[idFieldName] as OdinKey)
        );
      }
    },
    [idFieldName, useDataCache]
  );

  const updateData = useCallback((data: ScrollerData, idFieldName: string) => {
    if (loadedData.current.length > 0) {
      for (let i = 0, length = data.length; i < length; i++) {
        const foundIndex = findDataIndex(loadedData.current, idFieldName, data[i][idFieldName] as SearchValueType);
        if (foundIndex === -1) {
          loadedData.current.push(data[i]);
        } else {
          // this will be used to determine which render pages need to be rebuilt
          updatedIndices.current.push(foundIndex);
          loadedData.current[foundIndex] = data[i];
        }
      }
    } else {
      // first page will just load without any checks
      loadedData.current.push(...data);
    }
  }, []);

  const calculateTotalPageCount = () => {
    // using JavaScript truthy/falsy if the calculation below returns 0 then it will be set to 1
    const fullPages = Math.trunc(loadedData.current.length / pageSize) || 1;
    return loadedData.current.length > pageSize && loadedData.current.length % pageSize >= minimumPageCount
      ? fullPages + 1
      : fullPages;
  };

  const resizeCallback = useCallback((entries: Array<ResizeObserverEntry>) => {
    for (let i = 0, length = entries.length; i < length; i++) {
      const resizePage = parseInt(entries[i].target.getAttribute('data-page') || '1');
      const pageHeight = entries[i].contentRect.height;

      if (pageHeight > 0) {
        pageHeights.current[resizePage] = pageHeight;
      }
    }
  }, []);

  const setIsDataLoading = useCallback((isDataLoading: boolean, resetRenderPages = false, setAllowGetData = true) => {
    const settings: ContinuousScrollerReducerAction = {
      type: ContinuousScrollerActionType.SetIsLoading,
      isLoading: isDataLoading,
    };

    if (resetRenderPages) {
      settings.renderPages = [];
    }

    dispatcher(settings);

    if (setAllowGetData) {
      allowGetDataCall.current = !isDataLoading;
    }
  }, []);

  /**
   * Helper utility validating that sorts or filters are marked as changed or
   * differ from the ones used previously to fetch the data.
   *
   * @param options Current options to compare with
   * @returns true if sorts or filters applied to data have changed
   */
  const isSortOrFilterChanged = useCallback((options: OdinDataRetrievalOptions) => {
    let isChanged = areFiltersChanged.current || areSortsChanged.current;

    if (!isChanged) {
      const filtersChanged = !deepEquals(previousCallOptions.current?.fieldFilters || {}, options.fieldFilters || {});
      const sortsChanged = !deepEquals(previousCallOptions.current?.sortedFields || {}, options.sortedFields || {});

      isChanged = filtersChanged || sortsChanged;
    }
    return isChanged;
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: _callGetData is defined after this callback; isSortOrFilterChanged is stable
  const checkCallGetData = useCallback(
    (options: OdinDataRetrievalOptions, resetRenderPages = false, forceCallGetData = false) => {
      if (
        dataResultSubscriber.current &&
        (allowGetDataCall.current || resetRenderPages) &&
        options.page &&
        (((lastFulfilledPage.current < options.page || isSortOrFilterChanged(options)) &&
          (canMakeDuplicateCall ||
            previousCallOptions.current === undefined ||
            !deepEquals(previousCallOptions.current, options))) ||
          invalidatedPage.current ||
          forceCallGetData)
      ) {
        _callGetData(options, resetRenderPages);
      }
    },
    [canMakeDuplicateCall]
  );

  /**
   * This is the actual function that makes the `getData` call. This should no longer be called directly, instead use
   * the `checkCallGetData` function.
   */
  const _callGetData = useCallback(
    debounce((options: OdinDataRetrievalOptions, resetRenderPages = false) => {
      if (dataResultSubscriber.current) {
        previousCallOptions.current = cloneDeep(options);
        setIsDataLoading(true, resetRenderPages);
        getDataRef.current(dataResultSubscriber.current, options);
      }
    }, 250),
    [setIsDataLoading]
  );

  const calculateRenderPages = () => {
    const startPage =
      currentPage.current - surroundingPagesCount >= 1 ? currentPage.current - surroundingPagesCount : 1;
    const endPage =
      currentPage.current + surroundingPagesCount <= totalPageCount.current
        ? currentPage.current + surroundingPagesCount
        : totalPageCount.current;

    const pages: Array<number> = [];
    for (let current = startPage, end = endPage; current <= end; current++) {
      pages.push(current);
    }

    if (startPage === 1 && endPage === 1 && pages.length === 0) {
      pages.push(1);
    } else if (startPage > 1 && pages.length < surroundingPagesCount * 2 + 1) {
      pages.splice(0, 0, startPage - 1);
    }

    dispatcher({
      type: ContinuousScrollerActionType.SetRenderPages,
      renderPages: pages,
    });
  };

  const intersectionCallback = (entries: Array<IntersectionObserverEntry>) => {
    if (lastRequestId.current !== undefined) {
      return;
    }

    const intersectingEntries = entries.filter((entry) => entry.isIntersecting);

    if (intersectingEntries.length) {
      const mainPage = intersectingEntries
        .filter((page) => (page.target as HTMLElement).dataset['name'] === 'page')
        .sort((a, b) => a.intersectionRatio - b.intersectionRatio);
      const requestDataMarker = intersectingEntries.filter(
        (page) => (page.target as HTMLElement).dataset['name'] === 'marker'
      );

      if (mainPage.length > 0) {
        const mainPageNumber = parseInt((mainPage[0].target as HTMLElement).dataset['page'] || '1');

        if (mainPageNumber !== currentPage.current) {
          currentPage.current = mainPageNumber;
          calculateRenderPages();
        }
      }

      if (requestDataMarker.length > 0) {
        if (lastFulfilledPage.current === currentPage.current) {
          requestedPage.current =
            areFiltersChanged.current || areSortsChanged.current ? 1 : lastFulfilledPage.current + 1;
          const options: OdinDataRetrievalOptions = {
            page: requestedPage.current,
            pageSize,
          };

          if (customProperties.current) {
            options.customProperties = customProperties.current;
          }
          if (savedFieldFilters.current) {
            options.fieldFilters = savedFieldFilters.current;
          }
          if (savedSortedFields.current) {
            options.sortedFields = savedSortedFields.current;
          }

          checkCallGetData(options);
        }
      }
    }
  };

  const calculateRenderPageIndices = useCallback(
    (renderPage: number) => {
      const startIndex = (renderPage - 1) * pageSize;
      const endIndex =
        renderPage === totalPageCount.current && renderPage * pageSize < loadedData.current.length
          ? renderPage * pageSize + (loadedData.current.length % pageSize)
          : renderPage * pageSize;

      return [startIndex, endIndex];
    },
    [pageSize]
  );

  const calculateRenderData = useCallback(
    (resetData?: boolean) => {
      const checkDataForUpdates = updatedIndices.current.length > 0;
      for (let i = 0, length = totalPageCount.current; i < length; i++) {
        const renderPage = i + 1;
        const [startIndex, endIndex] = calculateRenderPageIndices(renderPage);
        const renderPageData = loadedData.current.slice(startIndex, endIndex);
        const calculatedRenderPage = calculatedRenderPages.current.get(renderPage);

        if (calculatedRenderPage) {
          if (
            resetData === true ||
            (removeItemIndex.current && removeItemIndex.current < endIndex) ||
            (checkDataForUpdates && updatedIndices.current.some((index) => index >= startIndex || index <= endIndex)) ||
            calculatedRenderPage.length !== renderPageData.length
          ) {
            calculatedRenderPages.current.set(renderPage, renderPageData);
          }
        } else {
          calculatedRenderPages.current.set(renderPage, renderPageData);
        }
      }

      updatedIndices.current = [];
      removeItemIndex.current = undefined;
    },
    [calculateRenderPageIndices]
  );

  /**
   * Fires when a marker is observed by the IntersectionObserver; this makes sure the `renderTrigger` state variable is
   * always up to date.
   */
  const onMarkerObserved = useEventDebounce((markerId: string) => {
    if (lastObserverMarker.current !== markerId) {
      lastObserverMarker.current = markerId;
      setRenderTrigger(!renderTrigger);
    }
  }, 200);

  useEffect(() => {
    return () => {
      // Ensure pending observer callbacks do not fire after unmount in non-browser test environments
      onMarkerObserved.cancel();
    };
  }, [onMarkerObserved]);

  const removeItem = (value: SearchValueType) => {
    const foundIndex = findDataIndex(loadedData.current, idFieldName, value);

    if (foundIndex > -1 && loadedData.current.length > 0) {
      loadedData.current.splice(foundIndex, 1);
      removeItemIndex.current = foundIndex;
      calculateRenderData();
      setRenderTrigger(!renderTrigger);
    }
  };

  const getAllData = () => loadedData.current;

  const sortLocalData = () => {
    const sortedFields = sortSignal?.value;

    // when sortLocation is not passed then searchLocation is used instead; this is the way the code previously worked
    if (sortedFields && hasLocalSortFields(sortedFields)) {
      loadedData.current = sortFunction(loadedData.current, sortedFields);
      itemsChanged?.(loadedData.current);
      calculateRenderData(true);
      calculateRenderPages();
    }
  };

  const recordChanged = (recordId: number | string) => {
    if (!useDataCache) {
      throw new Error('This method needs useDataCache set to true');
    }

    let pageChanged = -1;
    for (const [page, recordIds] of pageIdCache.current.entries()) {
      if (recordIds.indexOf(recordId) > -1) {
        pageChanged = page;
        break;
      }
    }

    if (previousCallOptions.current && pageChanged > -1) {
      invalidatedPage.current = pageChanged;
      checkCallGetData(
        cloneObject(previousCallOptions.current, {
          page: pageChanged,
        }),
        true,
        true
      );
    }
  };

  const getDataOverwritePageCache = () => {
    overwritePageCache.current = true;
    allowGetDataCall.current = true;
    lastFulfilledPage.current = 0;

    if (previousCallOptions.current) {
      checkCallGetData(
        cloneObject(previousCallOptions.current, {
          page: 1,
        }),
        true,
        true
      );
    }
  };

  const recallGetData = (resetRenderPages = false) => {
    if (dataResultSubscriber.current) {
      setIsDataLoading(true, resetRenderPages);
      dataRetriever.getData(dataResultSubscriber.current, previousCallOptions.current);
    }
  };

  const stopPageRequests = () => {
    allowGetDataCall.current = false;
    isLastPageLoaded.current = true;
    lastFulfilledPage.current = totalPageCount.current;
  };

  const __dangerouslyResetScrollerData = () => {
    resetScrollerData([]);
  };

  const getClientWidth = () => {
    return containerRef.current?.clientWidth;
  };

  const getContainerReference = () => {
    return containerRef.current;
  };

  useImperativeHandle(ref, () => ({
    removeItem,
    getAllData,
    sortLocalData,
    recordChanged,
    getDataOverwritePageCache,
    recallGetData,
    stopPageRequests,
    __dangerouslyResetScrollerData,
    getClientWidth,
    getContainerReference,
  }));

  const renderPagesMarkup = (pages: Array<number>) => {
    const finalMarkup: Array<ReactNode> = [];
    for (let page = 1, length = totalPageCount.current; page <= length; page++) {
      const isRenderPage = pages.includes(page);
      const height = !isRenderPage ? pageHeights.current[page] : undefined;

      let data: TypeOrArray<ReactNode>;
      let dataTop: Array<ReactNode> = [];
      let dataBottom: Array<ReactNode> = [];
      if (isRenderPage && nextPageIndex.current) {
        const [startIndex] = calculateRenderPageIndices(page);
        const pageData = calculatedRenderPages.current.get(page) || [];
        const isLastPage = isLastPageLoaded.current && page === totalPageCount.current;

        data = children(pageData, startIndex, page, isLastPage);
        if (Array.isArray(data)) {
          dataTop = data.slice(0, nextPageIndex.current - 1);
          dataBottom = data.slice(nextPageIndex.current - 1, pageData.length);
        }
      }

      finalMarkup.push(
        <ScrollingDataPage
          key={`page_${page}`}
          pageNumber={page}
          height={height}
          intersectionObserver={observerRef.current}
          resizeObserver={resizeObserver.current}
          isPaginated={isPaginated}
        >
          {isRenderPage && dataTop.length && dataBottom.length ? (
            <>
              {dataTop}
              <ScrollerPageMarker
                intersectionObserver={observerRef.current}
                page={page}
                onMarkerObserved={onMarkerObserved}
              />
              {dataBottom}
              <ScrollerPageMarker
                intersectionObserver={observerRef.current}
                page={page}
                onMarkerObserved={onMarkerObserved}
              />
            </>
          ) : (
            <>
              {data}
              {data && (
                <ScrollerPageMarker
                  intersectionObserver={observerRef.current}
                  page={page}
                  onMarkerObserved={onMarkerObserved}
                />
              )}
            </>
          )}
        </ScrollingDataPage>
      );
    }

    return finalMarkup;
  };

  const scrollerClass = clsx(className, 'custom-scroll overflow-y-auto');

  return (
    <div ref={containerRef} className={scrollerClass} style={scrollerStyles}>
      <div>
        {!state.searchResults && state.renderPages && renderPagesMarkup(state.renderPages)}
        {dataLoadIndicator && state.isLoading && <div className="w-full">{dataLoadIndicator}</div>}
      </div>
      {state.searchResults && children(state.searchResults, state.searchResults.length, 1)}
    </div>
  );
}

export default ContinuousScroller;
